import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import { fetchProfilesByUserId } from "../lib/userProfile"

type Team = {
  _id: string
  teamId: string
  teamName: string
  enabled: boolean
}

type Props = {
  userId: string
  userName: string
  email: string
  onComplete: () => void
}

export function TeamSelectionModal({ userId, userName, email, onComplete }: Props) {
  const { refreshUserProfile } = useAuth()
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeamId, setSelectedTeamId] = useState("")
  const [inputUserName, setInputUserName] = useState(userName || "")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadTeams()
  }, [])

  const loadTeams = async () => {
    try {
      const result = await lumi.entities.teams.list({
        filter: { enabled: true },
        sort: { teamName: 1 }
      })
      setTeams(result.list as Team[])
    } catch (error) {
      console.error("Failed to load teams:", error)
      alert("❌ 팀 목록 로딩에 실패했습니다.")
    }
  }

  const handleSubmit = async () => {
    if (!inputUserName.trim()) {
      alert("⚠️ 이름을 입력해주세요.")
      return
    }
    if (!selectedTeamId) {
      alert("⚠️ 팀을 선택해주세요.")
      return
    }

    const selectedTeam = teams.find(t => t.teamId === selectedTeamId)
    if (!selectedTeam) return

    try {
      setLoading(true)

      // 기존 프로필 전체 조회 (limit 1 미사용 → 중복 감지 가능)
      const lookup = await fetchProfilesByUserId(userId)

      // 조회 자체가 실패하면 신규 생성하지 않는다.
      // (일시적 오류로 중복 프로필이 만들어지는 것을 차단)
      if (!lookup.ok) {
        alert("❌ 사용자 정보 조회에 실패했습니다.\n\n잠시 후 다시 시도해주세요.")
        setLoading(false)
        return
      }

      const profileData = {
        userId,
        email,
        userName: inputUserName.trim(),
        teamId: selectedTeam.teamId,
        teamName: selectedTeam.teamName,
        hasSelectedTeam: true,
        enabled: true,
        updatedAt: new Date().toISOString()
      }

      if (lookup.primary) {
        // 기존 대표 프로필 업데이트 (approvalStatus 및 role은 유지)
        await lumi.entities.userProfiles3.update(lookup.primary._id, profileData)

        // 중복 레코드가 있으면 팀/이름을 함께 맞춰 두어,
        // 어느 레코드가 조회되더라도 동일하게 동작하도록 보정한다.
        if (lookup.hasDuplicates) {
          console.warn(`⚠️ 중복 프로필 ${lookup.list.length}건 감지 - 동기화 진행`)
          const others = lookup.list.filter((p) => p._id !== lookup.primary!._id)
          await Promise.all(
            others.map((p) =>
              lumi.entities.userProfiles3
                .update(p._id, profileData)
                .catch((e: unknown) => console.error("중복 프로필 동기화 실패:", p._id, e))
            )
          )
        }
      } else {
        // 새 프로필 생성 직전 재확인 (동시 요청으로 인한 중복 생성 방지)
        const recheck = await fetchProfilesByUserId(userId)

        if (!recheck.ok) {
          alert("❌ 사용자 정보 확인에 실패했습니다.\n\n잠시 후 다시 시도해주세요.")
          setLoading(false)
          return
        }

        if (recheck.primary) {
          // 그 사이에 생성된 프로필이 있으면 생성 대신 업데이트
          await lumi.entities.userProfiles3.update(recheck.primary._id, profileData)
        } else {
          await lumi.entities.userProfiles3.create({
            ...profileData,
            approvalStatus: "pending",
            role: "USER",
            createdAt: new Date().toISOString()
          })
        }
      }

      // useAuth 상태 갱신
      if (refreshUserProfile) {
        await refreshUserProfile()
      }

      alert("✅ 팀 선택이 완료되었습니다!")
      onComplete()
    } catch (error) {
      console.error("Failed to save team selection:", error)
      alert("❌ 팀 선택 저장에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">초기 정보 입력</h2>
          <p className="text-sm sm:text-base text-gray-600">이름과 소속 팀을 입력해주세요</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={inputUserName}
              onChange={(e) => setInputUserName(e.target.value)}
              placeholder="홍길동"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              소속 팀 <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">팀을 선택하세요</option>
              {teams.map((team) => (
                <option key={team._id} value={team.teamId}>
                  {team.teamName}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !selectedTeamId || !inputUserName.trim()}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {loading ? "저장 중..." : "확인"}
          </button>
        </div>
      </div>
    </div>
  )
}
